import { Alert, Anchor, Button, Checkbox, Modal, Select, Stack, Text, TextInput, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CONTACT_CHANNEL_OPTIONS,
  CONTACT_CHANNEL_PHONE,
  SUBMIT_ATTESTATION_LABEL,
  SUBMIT_NO_CONTACT_ALERT,
  SUBMISSION_ATTESTATION_VERSION,
  attestationCopy,
} from "../constants/matching";
import { useAuth } from "../context/useAuth";
import { isEmailVerified, isIdentityVerified, isPhoneVerified } from "../lib/accountVerification";
import { candidatesApi, jobsApi, submissionsApi } from "../lib/api";
import { formatDate } from "../lib/format";
import { trackEvent } from "../lib/observability";
import { JobInsightsBanner } from "./jobs/JobInsightsBanner";
import { ApiError } from "../lib/apiClient";

export const SUBMIT_CANDIDATE_TITLE = "Submit a candidate";
export const SUBMIT_CANDIDATE_ACTION = "Submit candidate";
export const SUBMIT_TO_JOB_LABEL = "Submit";
export const SUBMIT_TO_JOB_BUTTON = "Submit to a job";
const SUBMITTED_TITLE = "Submitted";
const SUBMITTED_MESSAGE = "Candidate submitted successfully.";
const SUBMIT_FAILED_TITLE = "Could not submit";
const SUBMIT_FAILED_FALLBACK = "Something went wrong";
const CANDIDATE_SELECT_LABEL = "Candidate";
const CANDIDATE_SELECT_PLACEHOLDER = "Select a candidate from your pool";
const PITCH_LABEL = "Pitch note (optional)";
const PITCH_PLACEHOLDER = "Why is this candidate a great fit?";
const SUBMIT_STAGE_HINT = "Once submitted, the candidate enters the job's hiring flow at the first stage.";
const CONTACT_CHANNEL_SELECT_DATA = [...CONTACT_CHANNEL_OPTIONS];
const VERIFY_EMAIL_COPY = "Verify your email on your profile.";
const VERIFY_PHONE_COPY = "Verify your phone on your profile.";
const GO_TO_PROFILE_LABEL = "Go to profile";
const NO_CANDIDATES_BEFORE = "You have no candidates yet.";
const ADD_A_CANDIDATE_LABEL = "Add a candidate";
const NO_CANDIDATES_AFTER = "first.";
const VERIFICATION_REQUIRED_TITLE = "Verification required";
const CONTACT_CHANNEL_LABEL = "How you contacted them";
const CONTACTED_ON_LABEL = "Contacted on";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

interface Props {
  jobId: string;
  opened: boolean;
  onClose: () => void;
  candidateId?: string;
  candidateName?: string;
}

export function SubmitCandidateModal({
  jobId,
  opened,
  onClose,
  candidateId: lockedCandidateId,
  candidateName,
}: Props) {
  const { user } = useAuth();
  const [candidateId, setCandidateId] = useState<string | null>(lockedCandidateId ?? null);
  const [pitchNote, setPitchNote] = useState("");
  const [channel, setChannel] = useState<string | null>(CONTACT_CHANNEL_PHONE);
  const [contactedOn, setContactedOn] = useState(todayIso());
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const identityVerified = isIdentityVerified(user);
  const emailVerified = isEmailVerified(user);
  const phoneVerified = isPhoneVerified(user);
  const resolvedCandidateId = lockedCandidateId ?? candidateId;

  const candidatesQuery = useQuery({
    queryKey: ["my-candidates", "for-submit"],
    queryFn: () => candidatesApi.list({ page_size: 100 }),
    enabled: opened && !lockedCandidateId,
  });
  const lockedCandidateQuery = useQuery({
    queryKey: ["candidate", lockedCandidateId],
    queryFn: () => candidatesApi.get(lockedCandidateId!),
    enabled: opened && !!lockedCandidateId,
  });
  const jobQuery = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => jobsApi.get(jobId),
    enabled: opened && !!jobId,
  });

  const selectedCandidate =
    lockedCandidateQuery.data ??
    (candidatesQuery.data?.items ?? []).find((row) => row.id === resolvedCandidateId);
  const hasLoggedContact = Boolean(selectedCandidate?.last_contacted_at);
  const jobTitle = jobQuery.data?.title ?? "this job";
  const copy = useMemo(
    () =>
      attestationCopy({
        candidate: candidateName || selectedCandidate?.full_name || "this candidate",
        date: formatDate(contactedOn),
        channel: channel ?? CONTACT_CHANNEL_PHONE,
        jobTitle,
      }),
    [candidateName, selectedCandidate?.full_name, contactedOn, channel, jobTitle],
  );
  const futureDate = contactedOn > todayIso();
  const canSubmit =
    Boolean(resolvedCandidateId) &&
    identityVerified &&
    confirmed &&
    Boolean(channel) &&
    Boolean(contactedOn) &&
    !futureDate;

  async function handleSubmit() {
    if (!resolvedCandidateId || !channel || !confirmed) return;
    setSubmitting(true);
    try {
      const submission = await submissionsApi.create({
        job_id: jobId,
        candidate_id: resolvedCandidateId,
        pitch_note: pitchNote || undefined,
        attestation: {
          confirmed: true,
          contact_channel: channel,
          contacted_on: contactedOn,
          version: SUBMISSION_ATTESTATION_VERSION,
        },
      });
      notifications.show({ color: "green", title: SUBMITTED_TITLE, message: SUBMITTED_MESSAGE });
      trackEvent("submission.attested", { channel, attestation_version: SUBMISSION_ATTESTATION_VERSION });
      queryClient.invalidateQueries({ queryKey: ["my-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["job-submissions", jobId] });
      queryClient.invalidateQueries({ queryKey: ["candidates"] });
      if (!lockedCandidateId) setCandidateId(null);
      setPitchNote("");
      setConfirmed(false);
      onClose();
      navigate(`/submissions/${submission.id}`);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : SUBMIT_FAILED_FALLBACK;
      notifications.show({ color: "red", title: SUBMIT_FAILED_TITLE, message });
    } finally {
      setSubmitting(false);
    }
  }

  const candidateOptions = (candidatesQuery.data?.items ?? []).map((c) => ({
    value: c.id,
    label: `${c.full_name} — ${c.phone}`,
  }));

  function handleClose() {
    if (!lockedCandidateId) setCandidateId(null);
    setPitchNote("");
    setConfirmed(false);
    onClose();
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={SUBMIT_CANDIDATE_TITLE} centered>
      <Stack>
        {!identityVerified && (
          <Alert color="orange" title={VERIFICATION_REQUIRED_TITLE}>
            {!emailVerified && user?.auth_provider !== "google" && (
              <Text size="sm">{VERIFY_EMAIL_COPY}</Text>
            )}
            {!phoneVerified && <Text size="sm">{VERIFY_PHONE_COPY}</Text>}
            <Anchor component={Link} to="/profile" mt="xs">
              {GO_TO_PROFILE_LABEL}
            </Anchor>
          </Alert>
        )}
        {lockedCandidateId ? (
          candidateName ? <Text fw="var(--axio-font-weight-semibold)">{candidateName}</Text> : null
        ) : (
          <>
            {candidateOptions.length === 0 && !candidatesQuery.isLoading && (
              <Alert color="blue">
                {NO_CANDIDATES_BEFORE}{" "}
                <Anchor component={Link} to="/candidates">
                  {ADD_A_CANDIDATE_LABEL}
                </Anchor>{" "}
                {NO_CANDIDATES_AFTER}
              </Alert>
            )}
            <Select
              label={CANDIDATE_SELECT_LABEL}
              placeholder={CANDIDATE_SELECT_PLACEHOLDER}
              data={candidateOptions}
              value={candidateId}
              onChange={setCandidateId}
              searchable
              disabled={candidatesQuery.isLoading || !identityVerified}
              data-testid="submit-candidate-select"
            />
          </>
        )}
        {jobId ? <JobInsightsBanner jobId={jobId} /> : null}
        <Textarea
          label={PITCH_LABEL}
          placeholder={PITCH_PLACEHOLDER}
          value={pitchNote}
          onChange={(e) => setPitchNote(e.currentTarget.value)}
          minRows={3}
          disabled={!identityVerified}
        />
        {!hasLoggedContact && resolvedCandidateId ? (
          <Alert color="orange">{SUBMIT_NO_CONTACT_ALERT}</Alert>
        ) : null}
        <Select
          label={CONTACT_CHANNEL_LABEL}
          data={CONTACT_CHANNEL_SELECT_DATA}
          value={channel}
          onChange={setChannel}
          disabled={!identityVerified}
        />
        <TextInput
          type="date"
          label={CONTACTED_ON_LABEL}
          value={contactedOn}
          max={todayIso()}
          onChange={(event) => setContactedOn(event.currentTarget.value)}
          disabled={!identityVerified}
        />
        <Checkbox
          data-testid="submit-attestation"
          label={copy}
          checked={confirmed}
          onChange={(event) => setConfirmed(event.currentTarget.checked)}
          disabled={!identityVerified}
        />
        <Text size="xs" c="dimmed">
          {SUBMIT_ATTESTATION_LABEL}. {SUBMIT_STAGE_HINT}
        </Text>
        <Button
          onClick={handleSubmit}
          loading={submitting}
          disabled={!canSubmit}
          color="violet"
          data-testid="submit-candidate"
        >
          {SUBMIT_CANDIDATE_ACTION}
        </Button>
      </Stack>
    </Modal>
  );
}
